import apiClient from './apiClient';
import logger from '../utils/logger';

const INVITE_REQUEST_API = '/api/public/invite-request';
const INVITE_TOKEN_API = '/api/public/invite';
const START_REGISTRATION_API = '/api/public/invite/start-registration';
const REGISTRATION_STATUS_API = '/api/public/invite/registration-status';

/**
 * Submits a "Vreau invitație" lead-capture form. Backend auto-emails the
 * requester with a link to /register?invite=1&email=... and persists the row
 * for admin follow-up.
 *
 * @param {{ email: string, phone?: string, name?: string, request: string }} payload
 * @returns {Promise<{ success: boolean, message: string }>}
 */
export const submitInviteRequest = async (payload) => {
  try {
    if (!payload || !payload.email) {
      return {
        success: false,
        message: 'Email-ul este obligatoriu.',
      };
    }

    const body = {
      Email: payload.email.trim(),
      Phone: payload.phone?.trim() || null,
      Name: payload.name?.trim() || null,
      Request: payload.request?.trim() || null,
    };

    const response = await apiClient.post(INVITE_REQUEST_API, body);
    return {
      success: response.data?.success ?? true,
      message: response.data?.message || 'Cererea ta a fost înregistrată.',
    };
  } catch (error) {
    logger.error('Invite request error:', error);
    return {
      success: false,
      message:
        error.response?.data?.message ||
        'Eroare la trimiterea cererii. Te rugăm să încerci din nou.',
    };
  }
};

/**
 * Validates an approved invite token. Returns the recipient details so the
 * paywalled /register form can prefill the email.
 *
 * @param {string} token
 * @returns {Promise<{ success: boolean, email?: string, name?: string, phone?: string, message?: string, consumed?: boolean }>}
 */
export const fetchInviteByToken = async (token) => {
  if (!token) return { success: false, message: 'Token lipsa.' };
  try {
    const response = await apiClient.get(
      `${INVITE_TOKEN_API}/${encodeURIComponent(token)}`,
    );
    return {
      success: response.data?.success ?? true,
      email: response.data?.email,
      name: response.data?.name,
      phone: response.data?.phone,
    };
  } catch (error) {
    logger.error('Invite token lookup error:', error);
    const data = error.response?.data;
    return {
      success: false,
      consumed: !!data?.consumed,
      message: data?.message || 'Invitatia nu a fost gasita sau a expirat.',
    };
  }
};

/**
 * Submit the paywalled-registration form. On success the backend has buffered
 * the form data and returns a Stripe Checkout URL to redirect the user to.
 *
 * @param {Object} payload
 * @returns {Promise<{ success: boolean, sessionUrl?: string, pendingId?: string, message?: string }>}
 */
export const startInviteRegistration = async (payload) => {
  try {
    const response = await apiClient.post(START_REGISTRATION_API, payload);
    return {
      success: response.data?.success ?? true,
      sessionUrl: response.data?.sessionUrl,
      pendingId: response.data?.pendingId,
    };
  } catch (error) {
    logger.error('Start registration error:', error);
    return {
      success: false,
      message:
        error.response?.data?.message ||
        'Eroare la initierea inregistrarii. Te rugam sa incerci din nou.',
    };
  }
};

/**
 * Polled by the onboarding/success page. Returns whether the Stripe webhook
 * has materialized the user row yet.
 */
export const fetchRegistrationStatus = async (pendingId) => {
  if (!pendingId) return { success: false, completed: false };
  try {
    const response = await apiClient.get(
      `${REGISTRATION_STATUS_API}/${encodeURIComponent(pendingId)}`,
    );
    return {
      success: response.data?.success ?? true,
      completed: !!response.data?.completed,
      status: response.data?.status,
      email: response.data?.email,
    };
  } catch (error) {
    return { success: false, completed: false };
  }
};
